import { useState, useEffect } from "react";
import { X, User, Envelope, Phone, ChatText, CheckCircle } from "@phosphor-icons/react";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GetInvolvedModal({ isOpen, onClose }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setForm({ name: "", email: "", phone: "", message: "" });
      setSuccess(false);
      setError("");
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    if (isOpen) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSuccess(true);
    } catch {
      setError(t("modal_error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputBase =
    "w-full px-3.5 py-2.5 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = {
    borderColor: "rgba(26,92,55,0.2)",
    backgroundColor: "#F7F5F0",
    color: "#1C211F",
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50"
        style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ pointerEvents: "none" }}
      >
        <div
          className="relative w-full max-w-lg rounded-2xl shadow-2xl flex flex-col"
          style={{ backgroundColor: "#fff", pointerEvents: "auto", maxHeight: "90vh" }}
        >
          {submitting && (
            <div
              className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-2xl"
              style={{ backgroundColor: "rgba(255,255,255,0.88)" }}
            >
              <div className="w-8 h-8 border-2 rounded-full animate-spin mb-3" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
              <p className="text-sm font-medium" style={{ color: "#1C211F" }}>{t("modal_submitting")}</p>
            </div>
          )}

          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-5 border-b flex-shrink-0"
            style={{ borderColor: "rgba(26,92,55,0.1)" }}
          >
            <div>
              <h2
                className="font-heading text-xl font-medium"
                style={{ color: "#1C211F" }}
              >
                {t("modal_heading")}
              </h2>
              <p className="text-xs mt-0.5" style={{ color: "#4A5450" }}>
                {t("modal_subheading")}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: "#4A5450" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <X size={20} weight="bold" />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 px-6 py-6">
            {success ? (
              /* Success state */
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: "rgba(26,92,55,0.1)" }}
                >
                  <CheckCircle size={32} weight="duotone" style={{ color: "#1A5C37" }} />
                </div>
                <h3
                  className="font-heading text-xl font-medium mb-2"
                  style={{ color: "#1C211F" }}
                >
                  {t("modal_success_heading")}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: "#4A5450" }}>
                  {t("modal_success_desc")}
                </p>
                <button
                  onClick={onClose}
                  className="mt-6 px-6 py-2.5 rounded-full text-sm font-medium transition-colors"
                  style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#14482B")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#1A5C37")
                  }
                >
                  {t("modal_close")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label
                    className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
                    style={{ color: "#4A5450" }}
                  >
                    {t("modal_name")}
                  </label>
                  <div className="relative">
                    <User
                      size={16}
                      weight="duotone"
                      className="absolute left-3 top-1/2 -translate-y-1/2"
                      style={{ color: "#7A8A84" }}
                    />
                    <input
                      required
                      type="text"
                      placeholder={t("modal_name_ph")}
                      value={form.name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name: e.target.value }))
                      }
                      className={inputBase}
                      style={{ ...inputStyle, paddingLeft: "2.25rem" }}
                      onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(26,92,55,0.2)")
                      }
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label
                    className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
                    style={{ color: "#4A5450" }}
                  >
                    {t("modal_email")}
                  </label>
                  <div className="relative">
                    <Envelope
                      size={16}
                      weight="duotone"
                      className="absolute left-3 top-1/2 -translate-y-1/2"
                      style={{ color: "#7A8A84" }}
                    />
                    <input
                      required
                      type="email"
                      placeholder={t("modal_email_ph")}
                      value={form.email}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, email: e.target.value }))
                      }
                      className={inputBase}
                      style={{ ...inputStyle, paddingLeft: "2.25rem" }}
                      onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(26,92,55,0.2)")
                      }
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label
                    className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
                    style={{ color: "#4A5450" }}
                  >
                    {t("modal_phone")}
                  </label>
                  <div className="relative">
                    <Phone
                      size={16}
                      weight="duotone"
                      className="absolute left-3 top-1/2 -translate-y-1/2"
                      style={{ color: "#7A8A84" }}
                    />
                    <input
                      required
                      type="tel"
                      placeholder={t("modal_phone_ph")}
                      value={form.phone}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, phone: e.target.value }))
                      }
                      className={inputBase}
                      style={{ ...inputStyle, paddingLeft: "2.25rem" }}
                      onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(26,92,55,0.2)")
                      }
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label
                    className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
                    style={{ color: "#4A5450" }}
                  >
                    {t("modal_message")}
                  </label>
                  <div className="relative">
                    <ChatText
                      size={16}
                      weight="duotone"
                      className="absolute left-3 top-3"
                      style={{ color: "#7A8A84" }}
                    />
                    <textarea
                      required
                      rows={4}
                      placeholder={t("modal_message_ph")}
                      value={form.message}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, message: e.target.value }))
                      }
                      className={inputBase}
                      style={{ ...inputStyle, paddingLeft: "2.25rem", resize: "none" }}
                      onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(26,92,55,0.2)")
                      }
                    />
                  </div>
                </div>

                {error && (
                  <p
                    className="text-xs px-3 py-2 rounded-lg"
                    style={{
                      color: "#B85C38",
                      backgroundColor: "rgba(184,92,56,0.08)",
                    }}
                  >
                    {error}
                  </p>
                )}
              </form>
            )}
          </div>

          {/* Footer — only shown when not in success state */}
          {!success && (
            <div
              className="px-6 py-4 border-t flex gap-3 flex-shrink-0"
              style={{ borderColor: "rgba(26,92,55,0.1)" }}
            >
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors"
                style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}
              >
                {t("modal_cancel")}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2.5 rounded-full text-sm font-medium transition-colors"
                style={{
                  backgroundColor: submitting ? "#4a8c62" : "#B85C38",
                  color: "#F7F5F0",
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!submitting) e.currentTarget.style.backgroundColor = "#964B2E";
                }}
                onMouseLeave={(e) => {
                  if (!submitting) e.currentTarget.style.backgroundColor = "#B85C38";
                }}
              >
                {submitting ? t("modal_submitting") : t("modal_submit")}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
